//* Libraries imports
import React from "react";
import { Plus, Edit2, Trash2, Columns, Printer, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApplications } from "../../../../../../hooks/applications/useApplications";
import { useAnswerSheets } from "../../../../../../hooks/applications/answerSheets/useAnswerSheets";


interface ToolbarProps {
  selectedElement: number | null;
  handleAddElement: () => void;
  handleEditElement: () => void;
  handleRemoveElement: () => void;
  recalcPages: () => void;
  testId?: string;
  diagramId?: string;
  printMode?: boolean;
  // Estados e setters vindos do componente pai
  isPolling: boolean;
  setIsPolling: (isPolling: boolean) => void;
  downloadUrl: string | null;
  setDownloadUrl: (url: string | null) => void;
  setIsPrinting: (isPrinting: boolean) => void;
}

export const Toolbar: React.FC<ToolbarProps> = ({
  selectedElement,
  handleAddElement,
  handleEditElement,
  handleRemoveElement,
  recalcPages,
  testId,
  diagramId,
  printMode = false,
  isPolling,
  setIsPolling,
  downloadUrl,
  setDownloadUrl,
  setIsPrinting, 
}) => {
  const token = localStorage.getItem("token") || "";
  const navigate = useNavigate();
  const { generateDiagram, isGeneratingDiagram } = useApplications(token);
  const { getStatusJob } = useAnswerSheets(token || "", testId);

  // Função para realizar o processo de geração e exibir o modal para download
  const handleDownloadZip = async () => {
    try {
      if (!testId) {
        console.error("ID do teste não disponível.");
        return;
      }

      // Inicia a geração do diagrama
      const diagramResponse: any = await generateDiagram({
        id: testId,
        diagramId: diagramId || "",
      });

      // Se o retorno já vier com o link do ZIP, mostra o modal
      if (diagramResponse.zip) {
        setDownloadUrl(diagramResponse.zip);
      }
      // Caso retorne um job ID, inicia o polling
      else if (diagramResponse.diagram_generation_id) {
        const jobId = diagramResponse.diagram_generation_id;
        setIsPolling(true);

        const intervalId = setInterval(async () => {
          try {
            const statusResponse = await getStatusJob(jobId);
            if (statusResponse.job_status === "completed") {
              clearInterval(intervalId);
              setIsPolling(false);
              if (statusResponse.pdf_url) {
                setDownloadUrl(statusResponse.pdf_url);
              } else {
                console.error(
                  "Link do ZIP não retornado após conclusão do job."
                );
              }
            } else if (statusResponse.job_status === "failed") {
              clearInterval(intervalId);
              setIsPolling(false);
              console.error("A geração do PDF/ZIP falhou.");
            }
          } catch (err) {
            clearInterval(intervalId);
            setIsPolling(false);
            console.error("Erro ao fazer polling do job:", err);
          }
        }, 3000); // Polling a cada 3 segundos
      } else {
        console.error("Nenhum retorno válido do generateDiagram.");
      }
    } catch (error) {
      console.error("Erro ao iniciar a geração do ZIP:", error);
    }
  };

  if (printMode) {
    return (
      <div
        className="mb-3 d-flex flex-wrap no-print justify-content-between"
        style={{ gap: "8px" }}
      >
        <button
          onClick={handleDownloadZip}
          className="btn btn-primary"
          disabled={isGeneratingDiagram}
        >
          <Printer size={18} /> Gerar Diagramação
        </button>
        <button onClick={() => navigate(-1)} className="btn btn-secondary">
          <ArrowLeft size={18} /> Voltar
        </button>
      </div>
    );
  }

  return (
    <>


      <div
        className="mb-3 d-flex flex-wrap hidethis no-print"
        style={{ gap: "8px" }}
      >
        <button onClick={handleAddElement} className="btn btn-success">
          <Plus size={18} /> Adicionar elemento
        </button>
        <button
          onClick={handleEditElement}
          className={`btn ${
            selectedElement !== null
              ? "btn-info hidethis"
              : "btn-secondary disabled hidethis"
          }`}
          disabled={selectedElement === null}
        >
          <Edit2 size={18} /> Editar elemento
        </button>
        <button
          onClick={handleRemoveElement}
          className={`btn ${
            selectedElement !== null
              ? "btn-danger hidethis"
              : "btn-secondary disabled hidethis"
          }`}
          disabled={selectedElement === null}
        >
          <Trash2 size={18} /> Remover
        </button>
        <button onClick={recalcPages} className="btn btn-warning hidethis">
          <Columns size={18} /> Atualizar Layout
        </button>
        <button
          onClick={handleDownloadZip}
          className="btn btn-primary hidethis"
          disabled={isGeneratingDiagram}
        >
          <Printer size={18} /> Imprimir Prova
        </button>
        <button onClick={() => navigate(-1)} className="btn btn-secondary">
          <ArrowLeft size={18} /> Voltar
        </button>
      </div>
    </>
  );
};

export default Toolbar;
